import React, { useState, useEffect, useMemo } from 'react';
import UnifiedTableCell from './unified-table-cell';
import { combineToUnifiedTableItems } from './unified-table-utils';
import { getSuggestedTasks } from '@/services/aiSuggestionsService';
import { getAllSOAControls } from '@/services/soaUpdatesService';
import { acceptSOAControlChanges } from '@/services/soaControlsService';
import { 
  SuggestedAssuranceTask, 
  NormalizedTableItem,
  UnifiedTableCellData,
  UpdateCellType
} from './types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Filter, ArrowUpDown } from 'lucide-react';

interface UnifiedTableExampleProps {
  tenancyId: string;
  soaControls?: any[];
  controlsLoaded?: boolean;
  onUpdate?: () => void;
  acceptAction?: (item: NormalizedTableItem) => Promise<void>;
  rejectAction?: (item: NormalizedTableItem) => Promise<void>;
}

const UnifiedTableExample: React.FC<UnifiedTableExampleProps> = ({ 
  tenancyId,
  soaControls: externalSOAControls = [],
  controlsLoaded: externalControlsLoaded = false,
  onUpdate,
  acceptAction,
  rejectAction,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [aiSuggestions, setAISuggestions] = useState<SuggestedAssuranceTask[]>([]);
  const [soaControls, setSOAControls] = useState<any[]>([]);
  const [unifiedItems, setUnifiedItems] = useState<NormalizedTableItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Source filtering state
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Staged loading states
  const [aiTasksLoaded, setAiTasksLoaded] = useState(false);
  const [controlsLoaded, setControlsLoaded] = useState(false);

  // Extract unique sources from AI suggestions (always alphabetical for filter)
  const availableSources = useMemo(() => {
    const sources = new Set<string>();
    aiSuggestions.forEach(suggestion => {
      if (suggestion.file_name) {
        sources.add(suggestion.file_name);
      }
    });
    const result = Array.from(sources).sort(); // Always alphabetical
    console.log('availableSources for filter:', result);
    return result;
  }, [aiSuggestions]);

  // Filter and sort items based on selected source and sort order
  const filteredItems = useMemo(() => {
    let items = unifiedItems;
    
    // Filter by source if selected
    if (selectedSource !== 'all') {
      items = items.filter(item => {
        if (item.itemType === 'ai_suggestion') {
          return item.documentName === selectedSource;
        }
        return true; // Always show SOA controls
      });
    }
    
    // Sort by source (only affects AI suggestions, SOA controls stay in place)
    const sortedItems = [...items].sort((a, b) => {
      // Only sort AI suggestions by source, keep SOA controls in original order
      if (a.itemType === 'ai_suggestion' && b.itemType === 'ai_suggestion') {
        const aSource = a.documentName || '';
        const bSource = b.documentName || '';
        const comparison = aSource.localeCompare(bSource);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      // If one is AI suggestion and one is SOA control, maintain original order
      if (a.itemType === 'ai_suggestion' && b.itemType !== 'ai_suggestion') {
        return 1; // Move AI suggestions after SOA controls
      }
      if (a.itemType !== 'ai_suggestion' && b.itemType === 'ai_suggestion') {
        return -1; // Keep SOA controls first
      }
      return 0; // Keep original order for SOA controls
    });
    
    console.log('Table items sorted:', sortedItems.length, 'items, sortOrder:', sortOrder);
    return sortedItems;
  }, [unifiedItems, selectedSource, sortOrder]);

  // Load AI suggestions first, then controls
  const loadAISuggestions = async () => {
    try {
      const suggestions = await getSuggestedTasks();
      console.log('Fetched AI suggestions:', suggestions);
      setAISuggestions(suggestions);
      setAiTasksLoaded(true);
      setIsLoading(false); // Hide loading once AI tasks are ready
    } catch (error) {
      console.error('Error loading AI suggestions:', error);
      setAiTasksLoaded(true); // Mark as loaded even on error
      setIsLoading(false);
    }
  };

  const loadSOAControls = async () => {
    // Use external controls if provided, otherwise fetch them
    if (externalSOAControls.length > 0) {
      console.log('Using external SOA controls:', externalSOAControls.length);
      setSOAControls(externalSOAControls);
      setControlsLoaded(externalControlsLoaded);
      return;
    }

    try {
      const controls = await getAllSOAControls(tenancyId, 200, true);
      console.log('Fetched SOA controls:', controls);
      setSOAControls(controls);
      setControlsLoaded(true);
    } catch (error) {
      console.error('Error loading SOA controls:', error);
      setControlsLoaded(true); // Mark as loaded even on error
    }
  };

  // Load both AI suggestions and SOA controls data (legacy function for refresh)
  const loadAllData = async () => {
    setIsLoading(true);
    setError(null);
    setAiTasksLoaded(false);
    setControlsLoaded(false);

    try {
      // Load AI suggestions first
      await loadAISuggestions();
      // Then load controls in background
      await loadSOAControls();
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again later.');
      setIsLoading(false);
    }
  };

  // Load data on initial render with staged loading
  useEffect(() => {
    const loadDataStaged = async () => {
      setIsLoading(true);
      setError(null);
      
      // Load AI suggestions first
      await loadAISuggestions();
      
      // Load controls in background
      loadSOAControls(); // Don't await - let it run in background
    };
    
    loadDataStaged();
  }, [tenancyId]);

  // Update controls when external controls change
  useEffect(() => {
    if (externalSOAControls.length > 0) {
      console.log('External SOA controls updated:', externalSOAControls.length);
      setSOAControls(externalSOAControls);
      setControlsLoaded(externalControlsLoaded);
    }
  }, [externalSOAControls, externalControlsLoaded]);

  // Recombine items whenever aiSuggestions or soaControls change
  useEffect(() => {
    if (aiTasksLoaded) {
      console.log('Recombining items - AI suggestions:', aiSuggestions.length, 'SOA controls:', soaControls.length);
      const combinedItems = combineToUnifiedTableItems(aiSuggestions, soaControls);
      console.log('Total combined items:', combinedItems.length);
      console.log('Combined items:', combinedItems);
      setUnifiedItems(combinedItems);
    }
  }, [aiSuggestions, soaControls, aiTasksLoaded]);

  // Handler for accepting AI suggestion items
  const handleAcceptItem = async (sourceId: string, item: NormalizedTableItem) => {
    try {
      console.log('Accepting AI suggestion item:', item);
      // Call the existing acceptSuggestedTaskItem from aiSuggestionsService here
      // This will be handled through the UnifiedTableCell component
      if (acceptAction) {
        await acceptAction(item);
        if (localStorage.getItem('aiSuggestionAccepted') === 'true') {
          localStorage.removeItem('aiSuggestionAccepted');
          await loadAISuggestions();
        }
      }
      
      // Immediately remove the accepted item from the UI
      setUnifiedItems(prevItems => {
        return prevItems.filter(prevItem => 
          !(prevItem.itemType === 'ai_suggestion' && prevItem.sourceId === sourceId && prevItem.originalId === item.originalId)
        );
      });
      
      // Refresh data after accepting
      if (onUpdate) onUpdate();
      else await loadAllData();
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error accepting item:', error);
      return Promise.reject(error);
    }
  };

  // Handler for rejecting AI suggestion items
  const handleRejectItem = async (sourceId: string, item: NormalizedTableItem) => {
    try {
      console.log('Rejecting AI suggestion item:', item);
      // Call the existing rejectAction from aiSuggestionsService here
      // This will be handled through the UnifiedTableCell component
      if (rejectAction) {
        await rejectAction(item);
      }
      
      // Immediately remove the rejected item from the UI
      setUnifiedItems(prevItems => {
        return prevItems.filter(prevItem => 
          !(prevItem.itemType === 'ai_suggestion' && prevItem.sourceId === sourceId && prevItem.originalId === item.originalId)
        );
      });
      
      // Refresh data after rejecting
      if (onUpdate) onUpdate();
      else await loadAllData();
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error rejecting item:', error);
      return Promise.reject(error);
    }
  };

  // Handler for accepting SOA control changes
  const handleAcceptSOAControl = async (controlId: string, assignee: string, status: string) => {
    try {
      console.log('Accepting SOA control changes:', { tenancyId, controlId, assignee, status });
      await acceptSOAControlChanges(tenancyId, controlId, assignee, status);
      
      // Immediately remove the accepted item from the UI
      setUnifiedItems(prevItems => {
        return prevItems.filter(item => 
          !(item.itemType === 'soa_control' && item.originalId === controlId)
        );
      });
      
      // Refresh data after accepting
      if (onUpdate) onUpdate();
      else await loadAllData();
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error accepting SOA control changes:', error);
      return Promise.reject(error);
    }
  };

  // Create unified cell data
  const cellData: UnifiedTableCellData = {
    id: 'unified-table',
    type: UpdateCellType.TABLE_TASK,
    data: {
      items: filteredItems,
      aiSuggestions,
      soaControls
    }
  };

  // Render loading state, error state, or table
  if (isLoading) {
    return <div className="p-8 text-center">Loading AI suggestions...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600">
        <div className="mb-4">
          {error}
        </div>
        <button 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={loadAllData}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (filteredItems.length === 0 && selectedSource === 'all') {
    return (
      <div className="p-8 text-center text-gray-500">
        No items to display
      </div>
    );
  }

  return (
    <div>
      {/* Source Filter - Always show when sources exist */}
      {availableSources.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Filter by source document" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources ({unifiedItems.length} items)</SelectItem>
              {availableSources.map((source) => {
                const count = unifiedItems.filter(item => 
                  item.itemType === 'ai_suggestion' && item.documentName === source
                ).length;
                return (
                  <SelectItem key={source} value={source}>
                    {source} ({count} items)
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          
          {/* Sort button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
              console.log('Sort button clicked, changing table sort from', sortOrder, 'to', newOrder);
              setSortOrder(newOrder);
              // No need to force dropdown re-render since we're sorting the table now
            }}
            className="h-10 px-3"
            title={`Sort table by source ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
          >
            <ArrowUpDown className={`h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
          </Button>
          
          {selectedSource !== 'all' && (
            <span className="text-sm text-gray-500">
              Showing {filteredItems.length} of {unifiedItems.length} items
            </span>
          )}
        </div>
      )}

      {/* Show empty state for filtered results */}
      {filteredItems.length === 0 && selectedSource !== 'all' && (
        <div className="p-8 text-center text-gray-500">
          <div className="mb-4">
            No items from source "{selectedSource}"
          </div>
          <Button 
            variant="outline" 
            onClick={() => setSelectedSource('all')}
          >
            Show All Sources
          </Button>
        </div>
      )}
      
      {/* Show table when there are items */}
      {filteredItems.length > 0 && (
        <>
          {!controlsLoaded && aiTasksLoaded && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                <span className="text-sm font-medium">
                  Controls loading in background... AI tasks ready for processing!
                </span>
              </div>
            </div>
          )}
          
          <UnifiedTableCell 
            cellData={cellData}
            onUpdate={loadAllData}
            onAcceptItem={handleAcceptItem}
            onRejectItem={handleRejectItem}
            onAcceptSOAControl={handleAcceptSOAControl}
          />
        </>
      )}
    </div>
  );
};

export default UnifiedTableExample;
